import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCreateSeedSender } from "@/hooks/use-mailbox";
import { cn, extractErrorMessage, validEmailSchema } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Plus, X } from "lucide-react";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AuthProvider } from "@/types/common";
import { espTypeDescription } from "@/types/common";

const MAX_SENDERS = 50;

const emailSchema = z.object({
  senders: z
    .array(
      z.object({
        firstName: z.string().min(1, "First name is required"),
        lastName: z.string().min(1, "Last name is required"),
        espType: z.nativeEnum(AuthProvider).optional(),
        email: validEmailSchema,
      })
    )
    .max(MAX_SENDERS),
});

export function AddSenderDialog() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { mutateAsync: createSeedSender, isPending } = useCreateSeedSender();

  const handleAddSenders = async (senders: Array<{ email: string; firstName: string; lastName: string; espType?: string }>) => {
    try {
      await createSeedSender(senders);
      toast.success("Senders added successfully.");
      window.cioanalytics?.track("sender_profiles_added", {
        count: senders.length,
        emails: senders.map(s => s.email),
      });
      setIsDialogOpen(false);
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to add senders");
      toast.error(message);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Sender
        </Button>
      </DialogTrigger>
      <DialogContent className="px-0">
        <DialogHeader className="px-6">
          <DialogTitle>Add Senders Profile</DialogTitle>
          <DialogDescription>
            Add email addresses to track their records. You can add up to{" "}
            {MAX_SENDERS} senders.
          </DialogDescription>
        </DialogHeader>
        <AddSenderForm
          setIsDialogOpen={setIsDialogOpen}
          onSubmit={handleAddSenders}
          isPending={isPending}
        />
      </DialogContent>
    </Dialog>
  );
}

const AddSenderForm = ({
  onSubmit,
  setIsDialogOpen,
  isPending,
}: {
  onSubmit: (senders: Array<{ email: string; firstName: string; lastName: string; espType?: string }>) => void;
  setIsDialogOpen: (isDialogOpen: boolean) => void;
  isPending: boolean;
}) => {
  const form = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      senders: [
        {
          firstName: "",
          lastName: "",
          email: "",
          espType: undefined,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "senders",
  });

  const handleClose = () => {
    setIsDialogOpen(false);
    form.reset({
      senders: [
        {
          firstName: "",
          lastName: "",
          email: "",
          espType: undefined,
        },
      ],
    });
  };

  const handleOnSubmit = (data: z.infer<typeof emailSchema>) => {
    const emails = data.senders.map((sender) => sender.email.toLowerCase());
    const hasDuplicates = new Set(emails).size !== emails.length;
    if (hasDuplicates) {
      toast.error("Duplicate email addresses are not allowed");
      return;
    }
    
    // Send full sender data including firstName, lastName, and espType
    const senders = data.senders.map((sender) => ({
      email: sender.email.toLowerCase(),
      firstName: sender.firstName || "",
      lastName: sender.lastName || "",
      espType: sender.espType,
    }));
    
    console.log("Sending senders data:", senders);
    onSubmit(senders);
  };
  
  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleOnSubmit)} className="h-full">
          <div className="max-h-[400px] overflow-y-auto h-full">
            <div className="space-y-4 px-6 py-2">
              {fields.map((field, index) => (
                <div key={field.id} className="space-y-3">
                  {/* Sender Profile Header */}
                  {fields.length > 1 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-muted-foreground">
                        Seed Account {index + 1}
                      </span>
                      {fields.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          onClick={() => remove(index)}
                          className="text-muted-foreground hover:text-foreground h-6 w-6"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}

                  {/* First Row: First Name and Last Name */}
                  <div className="flex gap-3">
                    <FormField
                      control={form.control}
                      name={`senders.${index}.firstName`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="First Name"
                              type="text"
                            />
                          </FormControl>
                          <FormMessage className="font-normal" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`senders.${index}.lastName`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Last Name"
                              type="text"
                            />
                          </FormControl>
                          <FormMessage className="font-normal" />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Second Row: Email and ESP Type */}
                  <div className="flex gap-3">
                    <FormField
                      control={form.control}
                      name={`senders.${index}.email`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="user@domain.com"
                              type="email"
                            />
                          </FormControl>
                          <FormMessage className="font-normal" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`senders.${index}.espType`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="ESP Type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-48">
                              {Object.entries(espTypeDescription)
                                .filter(([key]) => {
                                  const k = key.toLowerCase();
                                  return k !== "gmail" && k !== "outlook" && k !== "yahoo";
                                })
                                .map(([key, value]) => (
                                  <SelectItem key={key} value={key}>
                                    {value}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <FormMessage className="font-normal" />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Divider between profiles */}
                  {index < fields.length - 1 && (
                    <div className="border-t border-border my-2" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </form>
      </Form>

      <div className="px-6 h-full">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            if (fields.length < MAX_SENDERS)
              append({
                firstName: "",
                lastName: "",
                email: "",
                espType: undefined,
              });
          }}
          className="w-full border-2 border-dashed border-muted-foreground/20 hover:border-primary/30 hover:bg-primary/5"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add more
        </Button>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              form.handleSubmit(handleOnSubmit)();
            }}
            className={cn("bg-primary hover:bg-primary/90")}
            disabled={fields.length === 0 || isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              "Add Senders"
            )}
          </Button>
        </div>
      </div>
    </>
  );
};
